/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, forkJoin, iif, Observable, of, Subscription, throwError } from 'rxjs';
import { faBook } from '@fortawesome/free-solid-svg-icons/faBook';
import { faHands } from '@fortawesome/free-solid-svg-icons/faHands';
import { faHandsHelping } from '@fortawesome/free-solid-svg-icons/faHandsHelping';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons/faArrowLeft';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons/faArrowRight';
import { faComment } from '@fortawesome/free-solid-svg-icons/faComment';
import { faCommentSlash } from '@fortawesome/free-solid-svg-icons/faCommentSlash';
import { faEye } from '@fortawesome/free-solid-svg-icons/faEye';
import { faSquare } from '@fortawesome/free-solid-svg-icons/faSquare';
import { mergeMap, skip, switchMap, take } from 'rxjs/operators';
import { RequestBodyData } from '../../../models/request-body.data';
import { InterventionInstanceInterface } from '../../../models/interface/intervention-instances/intervention-instance.interface';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { LessonInterface } from '../../../models/interface/lesson.interface';
import { PayloadInterface } from '../../../models/interface/payload.interface';
import { AlertService } from '../../../services/alert/alert.service';
import { InterventionInstanceConfigurationInterface } from '../../../models/interface/intervention-instances/intervention-instance.configuration.interface';
import { SkillInterface } from '../../../models/interface/skill.interface';
import { DiaryInterface } from '../../../models/interface/diary.interface';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { HelperSkillService } from '../../../services/helper/helper-skill/helper-skill.service';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { StudyActionTypes } from '../../../store/study/study.action';
import { DiaryStore } from '../../../store/diary/component-store/diary.store';
import { InterventionInstanceStore } from '../../../store/intervention-instance/component-store/intervention-instance.store';
import { SkillStore } from '../../../store/skill/component-store/skill.store';
import { UserStore } from '../../../store/user/component-store/user.store';
import { StudyInterface } from '../../../models/interface/study/study.interface';

/**
 * Component:
 * Instance update confirmation displaying instance details to confirm the settings of the intervention;
 */
@Component({
  selector: 'app-instance-update-confirmation',
  templateUrl: './instance-update-confirmation.component.html',
  styleUrls: ['./instance-update-confirmation.component.scss'],
  providers: [DiaryStore, InterventionInstanceStore, SkillStore, UserStore],
})
export class InstanceUpdateConfirmationComponent implements OnInit, OnDestroy {
  // Icon
  faArrowLeft = faArrowLeft;
  faArrowRight = faArrowRight;
  faComment = faComment;
  faCommentSlash = faCommentSlash;
  faHandsHelping = faHandsHelping;
  faHands = faHands;
  faBook = faBook;
  faEye = faEye;
  faSquare = faSquare;

  public instancePayload: PayloadInterface = null;
  public instanceAttributes: {
    ecoach_ids: number | number[];
    patient_id: number;
    intervention_id: number;
    intervention_type: string;
    configuration: {
      questionnaire_configuration: InterventionInstanceConfigurationInterface[];
      custom_order: number[];
      starting_at: number;
    };
  } = null;

  public myUserId: number;

  public intervention: InterventionInterface;
  public studyMembers: UserInterface[] = [];
  public studyCollaborator: UserInterface[] = [];
  public instance: InterventionInstanceInterface;
  public formCustomOrder: number[] = [];
  public unlockType: string[] = ['ALWAYS', 'MANUALLY', 'AFTER_PREVIOUS', 'AT_DATE'];

  public unconfirmedSubject = new BehaviorSubject<boolean>(true);

  public isManager$: Observable<boolean>;

  public questionnaires: LessonInterface[] = [];
  public skills: SkillInterface[] = [];

  public diaries: DiaryInterface[] = [];
  public diariesOfStudy$: Observable<DiaryInterface[]>;

  public updateInstanceResponse$: Observable<any>;

  public skillsOfIntervention$: Observable<SkillInterface[]>;

  private addEcoachesToInterventionInstanceResponse$: Observable<any>;
  private deleteEcoachesToInterventionInstanceResponse$: Observable<any>;

  // Element id
  private elementId: string;
  private isManagerSubject = new BehaviorSubject<boolean>(false);

  private subscriptions: Subscription[] = [];

  constructor(
    private actions$: Actions,
    private alertService: AlertService,
    private helperService: HelperService,
    private helperSkillService: HelperSkillService,
    private helperDialogService: HelperDialogService,
    private diaryStore: DiaryStore,
    private interventionInstanceStore: InterventionInstanceStore,
    private skillStore: SkillStore,
    private userStore: UserStore,
    private store: Store<{ skills: SkillInterface[] }>,
  ) {
    this.isManager$ = this.isManagerSubject.asObservable();
    this.diariesOfStudy$ = this.diaryStore.diariesOfStudy$;
    this.updateInstanceResponse$ = this.interventionInstanceStore.updateInstanceResponse$;
    this.skillsOfIntervention$ = this.skillStore.skillsOfIntervention$;
    this.addEcoachesToInterventionInstanceResponse$ = this.userStore.addEcoachesToInterventionInstanceResponse$;
    this.deleteEcoachesToInterventionInstanceResponse$ = this.userStore.deleteEcoachesToInterventionInstanceResponse$;
  }

  // Helper function
  public get helper() {
    return this.helperService;
  }

  public get helperSkill() {
    return this.helperSkillService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  public get alert() {
    return this.alertService;
  }

  @Input()
  set _id(_id: string) {
    this.elementId = _id;
  }

  // Set study and its configuration from parent
  @Input()
  set _intervention(_intervention: InterventionInterface) {
    this.intervention = _intervention;
  }

  // Set members of study from parent
  @Input()
  set _members(_members: UserInterface[]) {
    this.studyMembers = _members;
  }

  // Set collaborators of study from parent
  @Input()
  set _collaborators(_collaborators: UserInterface[]) {
    this.studyCollaborator = _collaborators;
  }

  // Set instance from parent
  @Input()
  set _instance(_instance: InterventionInstanceInterface) {
    this.instance = _instance;
  }

  @Input()
  set _payload(_payload: PayloadInterface) {
    this.instancePayload = _payload;
    this.instanceAttributes = _payload.data.attributes;
  }

  @Input()
  set _questionnaires(_questionnaires: LessonInterface[]) {
    this.questionnaires = _questionnaires;
  }

  @Input()
  set _myUserId(_myUserId: number) {
    this.myUserId = _myUserId;
  }

  ngOnInit(): void {
    this.store.dispatch({
      type: StudyActionTypes.getCollaboratingStudiesType,
      payload: { studyId: this.intervention.attributes.study_id, include: 'roles' }
    });
    this.subscriptions.push(
      this.actions$
        .pipe(
          ofType(StudyActionTypes.getCollaboratingStudiesSuccessType, StudyActionTypes.getCollaboratingStudiesErrorType),
          take(1),
          mergeMap((result: any) =>
            iif(
              () => result.type === StudyActionTypes.getCollaboratingStudiesSuccessType,
              of(result),
              throwError(() => result),
            ),
          ),
          switchMap((result: any) => {
            const studies: StudyInterface[] = result['response']['body']['data'];
            const myRoleSlug = this.helper.getHighestRoleOfStudy(studies[0]);
            this.isManagerSubject.next(myRoleSlug === 'study.ecoachmanager' || myRoleSlug === 'study.owner');
            this.skillStore.getSkillsOfIntervention({ id: this.intervention.id });
            return this.skillsOfIntervention$;
          }),
          switchMap((result) => {
            this.skills = result;
            this.diaryStore.getDiariesOfStudy({ studyId: this.intervention.attributes.study_id });
            return this.diariesOfStudy$.pipe(skip(1), take(1));
          })
        )
        .subscribe({
          next: (result: any) => {
            this.diaries = result;
          },
          error: () => {
            this.diaries = [];
          },
        })
    );

    this.formCustomOrder = this.instanceAttributes.configuration.custom_order;
    this.instanceAttributes.configuration.questionnaire_configuration.forEach((config) => {
      if (config.unlock_type === 'at_date' && config.unlock_date < this.instanceAttributes.configuration.starting_at) {
        config.unlock_date = this.instanceAttributes.configuration.starting_at;
      }
    });
  }

  public getQuestionnaireById(questionnaireId: number): LessonInterface {
    if (questionnaireId !== undefined) {
      const qreId = questionnaireId.toString();
      const found = this.questionnaires.find(
        (questionnaire: LessonInterface) => questionnaire.id.toString() === qreId,
      );
      return found !== undefined ? found : null;
    }
    return null;
  }

  public getConfigurationByQuestionnaireId(questionnaireId: number): InterventionInstanceConfigurationInterface {
    const questionnaireConfig = this.instanceAttributes.configuration.questionnaire_configuration;
    const qreId = questionnaireId.toString();
    const config = questionnaireConfig.find((cfg) => cfg.id.toString() === qreId);
    return config !== undefined ? config : null;
  }

  public updateInterventionInstance(instanceId: number): Observable<any> {
    const removeIds: number[] = [];
    const addIds: number[] = [];
    const previousIds = this.instance.attributes.ecoach_id.filter(
      (id) => id.toString() !== this.myUserId.toString(),
    );
    const newIds: number[] = this.instancePayload.data.attributes.ecoach_ids.filter(
      (id: number) => id.toString() !== this.myUserId.toString(),
    );
    previousIds.forEach((id) => {
      const found = newIds.find((foundId) => foundId.toString() === id.toString());
      if (found === undefined) {
        removeIds.push(parseInt(id.toString(), 10));
      }
    });
    newIds.forEach((id) => {
      const found = previousIds.find((foundId) => foundId.toString() === id.toString());
      if (found === undefined) {
        addIds.push(parseInt(id.toString(), 10));
      }
    });
    const reqs: Array<Observable<any>> = [];

    if (removeIds.length !== 0) {
      const payloadRemove: PayloadInterface = new RequestBodyData('intervention_instance', { ecoach_ids: removeIds });
      this.userStore.deleteEcoachesToInterventionInstance({ instanceId: this.instance.id, payload: payloadRemove });
      reqs.push(this.deleteEcoachesToInterventionInstanceResponse$.pipe(skip(1), take(1)));
    }
    if (addIds.length !== 0) {
      const payloadAdd: PayloadInterface = new RequestBodyData('intervention_instance', { ecoach_ids: addIds });
      this.userStore.addEcoachesToInterventionInstance({ instanceId: this.instance.id, payload: payloadAdd });
      reqs.push(this.addEcoachesToInterventionInstanceResponse$.pipe(skip(1), take(1)));
    }
    const res: Observable<any> = reqs.length > 0 ? forkJoin(reqs) : of(true);
    return res.pipe(
      take(1),
      mergeMap(() => {
        if (this.isManagerSubject.value) {
          this.interventionInstanceStore.updateInstanceAsECoachManager({ payload: this.instancePayload, instanceId });
        } else {
          this.interventionInstanceStore.updateInstanceAsECoach({ payload: this.instancePayload, instanceId });
        }
        return this.updateInstanceResponse$.pipe(skip(1), take(1));
      })
    );
  }

  public showUnlockHint(config: InterventionInstanceConfigurationInterface): boolean {
    if (config.unlock_type !== 'after_previous') {
      return false;
    }
    // Previous position based on form_custom_order
    const qreId = config.id.toString();
    const currentPos = this.instanceAttributes.configuration.custom_order.findIndex(
      (value) => value.toString() === qreId,
    );
    const previousPos = currentPos - 1;
    if (previousPos < 0) {
      return false;
    }
    const predecessor = this.getConfigurationByQuestionnaireId(
      this.instanceAttributes.configuration.custom_order[previousPos],
    );
    return !!predecessor?.feedback_required;
  }

  public getAlreadyStarted(questionnaireId: number): number {
    if (this.instance.attributes.progress.active_questionnaires) {
      return this.instance.attributes.progress.active_questionnaires.find(
        (value) => value.toString() === questionnaireId.toString(),
      );
    }
    return null;
  }

  public openDialogDiaryPreview(diary: DiaryInterface) {
    this.helperDialog
      .openDialogDiaryPreview(diary)
      .afterClosed()
      .subscribe(() => {});
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
